<template>
    <div>
        <ModeSwitcher
            ref="modeSwitcher"
            :main-mode-text="$t('create')"
            :create-mode-text="$t('product.info.create_category')"
            :edit-mode-text="$t('product.info.edit_category')"
            @reset="editCatalogueId = null"
        >
            <template v-slot:btn-block>
                <div class="full-width d-flex align-center">
                    <v-spacer />
                    <FilterStatusToggle v-model="activeFilter" />
                </div>
            </template>
            <template v-slot:main-mode>
                <v-data-table
                    :headers="useHeaders"
                    :items="useFilteredCatalogue"
                    :items-per-page="-1"
                    :items-per-page-options="[-1]"
                    :items-length="useFilteredCatalogue.length"
                    :sort-by="[{key: 'name', order: 'asc'}]"
                    must-sort
                    density="compact"
                    color="primary"
                    class="elevation-1 table-server"
                    data-cy="catalogue-table"
                >
                    <template v-slot:item.name="{item}">
                        <div class="text-link" @click="editCatalogue(item.id)">
                            {{ item.name }}
                        </div>
                    </template>
                    <template v-slot:item.active="{item}">
                        {{ item.active ? $t('active') : $t('inactive') }}
                    </template>
                </v-data-table>
            </template>
            <template v-slot:additional-mode>
                <v-row dense>
                    <v-col cols="12" md="5">
                        <InfoCatalogueForm
                            :edit-catalogue-id="editCatalogueId"
                            :contract-id="contractId"
                            @save="save()"
                            @cancel="modeSwitcher.goToTable()"
                        />
                    </v-col>
                </v-row>
            </template>
        </ModeSwitcher>
    </div>
</template>

<script setup lang="ts">
    import InfoCatalogueForm from '~/components/products/information/InfoCatalogueForm.vue'
    import {contractServiceType} from '~/utils/types/types'

    type HeaderItemType = {
        title: string
        value: string
        width?: string
        minWidth?: string
    }

    const props = defineProps<{
        contractId: number
    }>()

    const modeSwitcher = ref(null)
    const editCatalogueId = ref<number>(null)
    const catalogue = ref<any[]>([])
    const activeFilter = ref<boolean>(true)

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        await loadCatalogue()
    })

    async function loadCatalogue(): Promise<void> {
        try {
            const rq = {contractId: supplierStore.fullContract?.id}
            const {categories} = await app.$api.ownProductServiceCategories.get(rq)
            catalogue.value = categories
                .filter(({defaultCategory}) => !defaultCategory)
                .sort((a, b) => a.name.localeCompare(b.name))
        } catch (e) {
            catalogue.value = []
        }
    }

    const save = async (): Promise<void> => {
        modeSwitcher.value.goToTable()
        editCatalogueId.value = null
        await loadCatalogue()
    }

    const useHeaders: ComputedRef<HeaderItemType[]> = computed(() => {
        const defaultHeader = [{title: t('status'), value: 'active', width: '140px', minWidth: '140px'}]
        if (supplierStore.contract?.serviceType === contractServiceType.concert) {
            return [{title: t('seats_categories'), value: 'name', sortable: true}, ...defaultHeader]
        }
        if (supplierStore.contract?.serviceType === contractServiceType.ownTransfer) {
            return [{title: t('transport'), value: 'name', sortable: true}, ...defaultHeader]
        }
        if (
            supplierStore.contract?.serviceType === contractServiceType.customComplexTour ||
            supplierStore.contract?.serviceType === contractServiceType.ownCruise
        ) {
            return [{title: t('category_name'), value: 'name', sortable: true}, ...defaultHeader]
        }
        return [{title: t('excursion_code'), value: 'name', sortable: true}, ...defaultHeader]
    })

    const editCatalogue = (id: number): void => {
        modeSwitcher.value.edit()
        editCatalogueId.value = id
    }

    const useFilteredCatalogue = computed(() => {
        return catalogue.value.filter(({active}) => activeFilter.value === active)
    })
</script>
